import {
  Button,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField
} from "@material-ui/core";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import DetailSection from "../../../components/DetailSection";

const MODE_PER_USER = "per_user";
const MODE_PER_UNIT = "per_unit";
const MODE_FLAT = "flat";

function resolveMode(options) {
  if (options?.billFlat != null && options.billFlat !== 0) return MODE_FLAT;
  if (options?.billPerUnit != null && options.billPerUnit !== 0)
    return MODE_PER_UNIT;
  return MODE_PER_USER;
}

function centsForMode(options, mode) {
  switch (mode) {
    case MODE_FLAT:
      return options?.billFlat;
    case MODE_PER_UNIT:
      return options?.billPerUnit;
    default:
      return options?.billPerUser;
  }
}

function sanitize(raw) {
  const onlyAllowed = raw.replace(/[^0-9.]/g, "");
  const [whole, ...rest] = onlyAllowed.split(".");
  return rest.length > 0 ? `${whole}.${rest.join("")}` : whole;
}

function addThousandsSeparators(intStr) {
  let out = "";
  for (let i = 0; i < intStr.length; i++) {
    if (i > 0 && (intStr.length - i) % 3 === 0) out += ",";
    out += intStr[i];
  }
  return out;
}

function formatLive(value) {
  if (value === "" || value == null) return "";
  const [whole, decimal] = value.split(".");
  const withCommas = addThousandsSeparators(whole);
  return decimal != null ? `${withCommas}.${decimal}` : withCommas;
}

export const CrmBillingSlice = ({ partner, saveAttribute }) => {
  const hasCrm = partner.features?.includes("boom_crm");
  const options = partner.crmBillingOptions;

  const [mode, setMode] = useState(MODE_PER_USER);
  const [amount, setAmount] = useState("");

  useEffect(() => {
    const initialMode = resolveMode(options);
    const initialCents = centsForMode(options, initialMode);
    setMode(initialMode);
    setAmount(initialCents != null ? (initialCents / 100).toFixed(2) : "");
  }, [options]);

  const handleModeChange = (e) => {
    const nextMode = e.target.value;
    setMode(nextMode);
    const stored = centsForMode(options, nextMode);
    setAmount(stored != null ? (stored / 100).toFixed(2) : "");
  };

  const handleSave = () => {
    const cents =
      amount !== "" ? Math.round(Number.parseFloat(amount) * 100) : null;
    const preserved = { phoneNumberMonthlyFee: options?.phoneNumberMonthlyFee ?? null };

    const crmBillingOptions = (() => {
      switch (mode) {
        case MODE_FLAT:
          return { ...preserved, billFlat: cents };
        case MODE_PER_UNIT:
          return { ...preserved, billPerUnit: cents };
        default:
          return { ...preserved, billPerUser: cents };
      }
    })();

    saveAttribute({ crmBillingOptions })
      .then(() => toast.success("CRM billing updated"))
      .catch(() => toast.error("Failed to update CRM billing"));
  };

  return (
    <DetailSection title="Boom CRM Pricing">
      <div className="flex gap-4 items-center flex-wrap">
        <TextField
          label="Cost Per Month"
          type="text"
          value={formatLive(amount)}
          onChange={(e) => setAmount(sanitize(e.target.value))}
          inputProps={{ inputMode: "decimal", style: { textAlign: "right" } }}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>
          }}
          variant="outlined"
          size="small"
          disabled={!hasCrm}
          style={{ minWidth: 220 }}
        />
        <RadioGroup row value={mode} onChange={handleModeChange}>
          <FormControlLabel
            value={MODE_PER_UNIT}
            control={<Radio color="primary" />}
            label="Per Unit"
            disabled={!hasCrm}
          />
          <FormControlLabel
            value={MODE_PER_USER}
            control={<Radio color="primary" />}
            label="Per User"
            disabled={!hasCrm}
          />
          <FormControlLabel
            value={MODE_FLAT}
            control={<Radio color="primary" />}
            label="Flat"
            disabled={!hasCrm}
          />
        </RadioGroup>
        <Button
          variant="contained"
          color="primary"
          disabled={!hasCrm}
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
    </DetailSection>
  );
};
